import {
  CONTENT_ID_CLAIMS_LOADED,
  CONTENT_ID_CLAIMS_CLEAR,
  CONTENT_ID_CLAIM_DETAILS_LOADED,
  CONTENT_ID_CLAIM_DETAILS_CLEAR,
  CONTENT_ID_CLAIM_HISTORY_LOADED,
  CONTENT_ID_CLAIM_HISTORY_CLEAR,
} from '../../actionTypes';
import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSecondaryFetchLoaderLoading,
  setSecondaryFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import { setFaviconLoading, setFaviconDefault } from '../../faviconActions';
import { userRefresh, refreshApi } from '../authActions';

// Reducer corresponding actions
function contentIdClaimsClear() {
  return { type: CONTENT_ID_CLAIMS_CLEAR };
}

function contentIdClaimsLoaded(claims) {
  return {
    type: CONTENT_ID_CLAIMS_LOADED,
    payload: { claims },
  };
}

function contentIdClaimDetailsClear() {
  return { type: CONTENT_ID_CLAIM_DETAILS_CLEAR };
}

function contentIdClaimDetailsLoaded(claimDetails) {
  return {
    type: CONTENT_ID_CLAIM_DETAILS_LOADED,
    payload: { claimDetails },
  };
}

function contentIdClaimHistoryClear() {
  return { type: CONTENT_ID_CLAIM_HISTORY_CLEAR };
}

function contentIdClaimHistoryLoaded(claimHistory) {
  return {
    type: CONTENT_ID_CLAIM_HISTORY_LOADED,
    payload: { claimHistory },
  };
}

// API
async function getContentIdClaimsApi(token, assetId, params, isExport = false) {
  if (isExport) {
    params.assetId = assetId;
    return api.getExport({
      path: 'youtube-cid/claims/export',
      token,
      params,
      version: 'gateway',
      filename: `content_id_claims_export_${assetId}`,
    });
  }

  const response = await api.get({
    path: `youtube-cid/assets/${assetId}/claims`,
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getContentIdAllClaimsApi(token, params, isExport = false) {
  if (isExport) {
    return api.getExport({
      path: 'youtube-cid/claims/export',
      token,
      params,
      version: 'gateway',
      filename: 'content_id_claims_export_all',
    });
  }

  const response = await api.get({
    path: 'youtube-cid/claims',
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getContentIdClaimDetailsApi(token, claimId) {
  const response = await api.get({
    path: `youtube-cid/claims/${claimId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function getContentIdClaimHistoryApi(token, claimId) {
  const response = await api.get({
    path: `youtube-cid/claims/${claimId}/history`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function postUpdateClaimSettingsApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/claims/update-settings',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

async function postMonetizationRequestApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/claims/monetization-requests',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

// Helper
async function fetchContentIdClaims(dispatch, assetId, params, token, isExport = false) {
  try {
    const claims = assetId
      ? await getContentIdClaimsApi(token, assetId, params, isExport)
      : await getContentIdAllClaimsApi(token, params, isExport);

    if (!isExport) {
      if (!assetId) dispatch(setFaviconDefault());

      dispatch(setFetchLoaderReset());
      dispatch(contentIdClaimsLoaded(claims));
    }

    if (isExport) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    if (!assetId) dispatch(setFaviconDefault());
    dispatch(setFetchLoaderReset());
    if (isExport) dispatch(setSubmissionLoaderReset());
  }
}

async function fetchContentIdClaimDetails(dispatch, claimId, token) {
  try {
    const claimDetails = await getContentIdClaimDetailsApi(token, claimId);
    dispatch(contentIdClaimDetailsLoaded(claimDetails));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFetchLoaderReset());
  }
}

async function fetchContentIdClaimHistory(dispatch, claimId, token) {
  try {
    const claimHistory = await getContentIdClaimHistoryApi(token, claimId);
    dispatch(contentIdClaimHistoryLoaded(claimHistory));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setSecondaryFetchLoaderReset());
  }
}

async function updateClaimsSettings(dispatch, token, body) {
  try {
    await postUpdateClaimSettingsApi(token, body);

    dispatch(arriveNotice('Claims settings updated successfully!', 'ok'));
    // Refresh user data to update featureConfig
    const json = await refreshApi(token);
    dispatch(userRefresh(json, token));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
  return null;
}

async function updateMonetizationStatusRequest(
  dispatch, token, body, refreshData, isRevertRequest = false,
) {
  try {
    await postMonetizationRequestApi(token, body);

    const message = isRevertRequest
      ? 'Revert request submitted, processing may take some time'
      : 'Request submitted successfully!';
    dispatch(arriveNotice(message, 'ok'));
    refreshData();
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
  return null;
}

// Public components actions
export function initiateFetchClaimsRequest(assetId, params, isExport = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    // Prevent API call if payment is pending
    if (paymentPending) return;

    if (!dataLoading && !isExport) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isExport) {
      dispatch(setSubmissionLoaderLoading());
    }

    await fetchContentIdClaims(dispatch, assetId, params, token, isExport);
  };
}

export function initiateFetchAllClaimsRequest(params, isExport = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    // Prevent API call if payment is pending
    if (paymentPending) return;

    if (!dataLoading && !isExport) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isExport) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isExport) {
      dispatch(setFaviconLoading());
    }

    await fetchContentIdClaims(dispatch, null, params, token, isExport);
  };
}

export function clearContentIdClaimsRequest() {
  return (dispatch) => {
    dispatch(contentIdClaimsClear());
  };
}

export function initiateFetchClaimDetailsRequest(claimId) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { dataLoading },
    } = getState();

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    await fetchContentIdClaimDetails(dispatch, claimId, token);
  };
}

export function clearContentIdClaimDetailsRequest() {
  return (dispatch) => {
    dispatch(contentIdClaimDetailsClear());
  };
}

export function initiateFetchClaimHistoryRequest(claimId) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { secondaryDataLoading },
    } = getState();

    if (!secondaryDataLoading) {
      dispatch(setSecondaryFetchLoaderLoading());
    }

    await fetchContentIdClaimHistory(dispatch, claimId, token);
  };
}

export function clearContentIdClaimHistoryRequest() {
  return (dispatch) => {
    dispatch(contentIdClaimHistoryClear());
  };
}

export function initiateUpdateClaimsSettingsRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();

    if (paymentPending || submissionLoading) return;
    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    await updateClaimsSettings(dispatch, token, body);
  };
}

export function initiateMonetizationRequest(body, refreshData, isRevertRequest = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();

    if (paymentPending || submissionLoading) return;
    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    await updateMonetizationStatusRequest(dispatch, token, body, refreshData, isRevertRequest);
  };
}
